import {
  ApplicationConfig,
  ɵenableProfiling as enableProfiling,
  ErrorHandler,
  Injector,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  RendererFactory2,
  inject,
  provideAppInitializer,
  APP_ID,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, withPreloading, PreloadAllModules } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_TIMEPICKER_CONFIG } from '@angular/material/timepicker';
import { importProvidersFrom } from '@angular/core';

import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { appInitialiserFactory } from './app-initialiser.factory';
import { routes } from './app.routes';
import { environment } from '@environments/environment';
import { ApiModule } from '@backend-client/api.module';
import { apiInterceptorFn } from './interceptors/api.interceptor';
import { DashboardErrorHandler } from '@classes/dashboard-error-handler';
import { InjectableEnvironmentService } from '@shared/services/injectable-environment-service';
import { EnvironmentService } from '@shared/services/environment.service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { ThemeService } from '@shared/services/theme.service';
import { RemoteConfigService } from './modules/shared/services/remote-config/remote-config.service';
import { CustomRouteReuseStrategy } from './classes/custom-route-reuse-strategy';
import { FirebaseInitialisationService } from '@firebase/firebase-initialisation.service';

if (isDevMode()) {
  enableProfiling();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([apiInterceptorFn])),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerImmediately',
    }),
    provideAppInitializer(() => {
      inject(FirebaseInitialisationService).initialise();
    }),
    importProvidersFrom(ApiModule.forRoot(environment.thriveUnifiedBackend)),
    provideTranslateService({
      loader: provideTranslateHttpLoader(),
    }),
    {
      provide: APP_ID,
      useValue: 'serverApp',
    },
    provideNativeDateAdapter(),
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    {
      provide: MAT_TIMEPICKER_CONFIG,
      useValue: { interval: '30 minutes' },
    },
    {
      provide: ErrorHandler,
      useClass: DashboardErrorHandler,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    provideAppInitializer(() => {
      const initializerFn = appInitialiserFactory(inject(TranslateService), inject(Injector));
      return initializerFn();
    }),
    {
      provide: InjectableEnvironmentService,
      useClass: EnvironmentService,
    },
    ThemeService,
    provideAppInitializer(() => {
      const initializerFn = (
        (environmentService: EnvironmentService, themeService: ThemeService, renderer: RendererFactory2) => () => {
          const theme = environmentService.getEnvironment().theme ?? 'default-theme';
          themeService.setTheme(theme, renderer.createRenderer(null, null));
        }
      )(inject(EnvironmentService), inject(ThemeService), inject(RendererFactory2));
      return initializerFn();
    }),
    RemoteConfigService,
    FeatureFlagService,
    provideAppInitializer(() => {
      const initializerFn = (
        (featureFlagService: FeatureFlagService, remoteConfigService: RemoteConfigService) => async () => {
          await remoteConfigService.initializeRemoteConfig();
          featureFlagService.initialise();
        }
      )(inject(FeatureFlagService), inject(RemoteConfigService));
      return initializerFn();
    }),
  ],
};
